import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import StatusBadge from "@/components/admin/StatusBadge";

export default function AdminDetailDrawer({ adminId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/management/admins/${adminId}`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(errMsg(e)); onClose(); })
      .finally(() => setLoading(false));
  }, [adminId, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-teal))]">
          <h2 className="font-heading uppercase tracking-[0.15em] text-white text-sm font-bold">Admin Details</h2>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="h-full grid place-items-center">
              <Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" size={24} />
            </div>
          ) : data ? (
            <div className="p-6 space-y-6">
              {/* Profile */}
              <div className="bg-[hsl(var(--zt-beige))]/50 rounded-2xl p-5 border border-[hsl(var(--zt-beige-deep))]">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[hsl(var(--zt-teal))] grid place-items-center text-white text-lg font-bold flex-shrink-0">
                    {(data.admin.name || data.admin.email || "A").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-[hsl(var(--zt-teal))]">{data.admin.name || "—"}</p>
                    <p className="text-sm text-[hsl(var(--zt-charcoal))]/60">{data.admin.email}</p>
                  </div>
                  <div className="ml-auto flex flex-col items-end gap-1">
                    <StatusBadge status={data.admin.role} />
                    <StatusBadge status={data.admin.status || "active"} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <InfoRow label="Phone" value={data.admin.phone} />
                  <InfoRow label="Department" value={data.admin.department} />
                  <InfoRow label="Last Login" value={fmtDate(data.admin.last_login_at)} />
                  <InfoRow label="Created" value={fmtDate(data.admin.created_at)} />
                  <InfoRow label="Account Locked" value={data.admin.account_locked ? "Yes" : "No"} />
                  <InfoRow label="Must Change Pwd" value={data.admin.must_change_password ? "Yes" : "No"} />
                </div>
              </div>

              {/* Audit Logs */}
              <div>
                <h3 className="font-heading uppercase tracking-[0.15em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold mb-3">
                  Activity Log
                </h3>
                {data.audit_logs.length === 0 ? (
                  <p className="text-sm text-[hsl(var(--zt-charcoal))]/40 text-center py-6">No activity recorded.</p>
                ) : (
                  <div className="space-y-2">
                    {data.audit_logs.map((log, i) => (
                      <div key={i} className="bg-[hsl(var(--zt-beige))]/40 rounded-xl px-4 py-3 border border-[hsl(var(--zt-beige-deep))]">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-heading uppercase tracking-wider text-[hsl(var(--zt-teal))] font-semibold">
                            {formatAction(log.action)}
                          </span>
                          <span className="text-[10px] text-[hsl(var(--zt-charcoal))]/45 whitespace-nowrap">
                            {fmtDate(log.created_at)}
                          </span>
                        </div>
                        <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/60 mt-0.5">
                          By {log.performed_by_name || "System"}
                          {log.ip_address ? ` · ${log.ip_address}` : ""}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/40 mb-0.5">{label}</p>
      <p className="text-sm text-[hsl(var(--zt-charcoal))]/80">{value || "—"}</p>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatAction(action) {
  return (action || "").replace(/_/g, " ");
}
